import React, { useState, useEffect, forwardRef } from 'react';
import Message from '../../common/Message';
import { InputFieldAddOnType } from '../../InputFieldV2/InputFieldAddOn';
import { showMaxCharWarning, showMaxCharError } from '../../util/showMessageType';
import {
  HelperText,
  CharCountMessage,
  MessageContainer,
  Asterisk,
  FormLabel,
  renderLink,
} from '../../form.styles';
import type { State as InputState, LabelLinkProps } from '../../form.styles';
import Eye from '../../../SvgIcons/Eye';
import HiddenEye from '../../../SvgIcons/HiddenEye';
import COLORS3 from '../../../../styles/emotion/colors3';
import {
  InputFieldContainer,
  Input,
  InputSection,
  OptionalText,
  StyledFormTooltip,
  HashtagAddOn,
  CalendarAddOn,
  GeoPinAddOn,
  LinkAddOn,
  CashAddOn,
  ClearIcon,
  EyeIconContainer,
  ClearIconContainer,
  CoupleSearchAddOn,
} from './InputFieldV3.styles';

export interface InputFieldV3Props extends React.InputHTMLAttributes<HTMLInputElement> {
  /** classname to be applied to wrapper div. To apply to <input>, pass as standard `className` prop */
  containerClassname?: string;
  value?: string;
  type?: string;
  disabled?: boolean;
  onChange?: React.FormEventHandler<HTMLInputElement>;
  defaultValue?: string;
  label?: string;
  /** passthrough props to HTML label element */
  labelProp?: React.HTMLAttributes<HTMLLabelElement>;
  labelLink?: LabelLinkProps | React.ReactElement;
  /** for showing red asterisk on label */
  isRequired?: boolean;
  /** shows helper text below input field */
  helperText?: string | JSX.Element;
  /** shows gray optional text within label */
  optionalText?: string | JSX.Element;
  /** shows tooltip on hover of info icon */
  tooltip?: string;
  /** pass classname to adjust tooltip width according to text */
  tooltipClassname?: string;
  /** adds 'x' to input field and allows clearing of value */
  clear?: boolean;
  onClear?: () => void;
  /** error message shown below input field; passed in by rff or redux form validation */
  errorMessage?: React.ReactNode;

  /** success message; needs to be passed in explicitly */
  successMessage?: React.ReactNode;

  /** display input length count, cycles through warning and error states based on increasing input length relative to maxChars.
   * NOTE: you will still need to add a max validation function. this prop is only responsible for the count display
   * */
  maxChars?: number;
  /** adds icon to the beginning of the field  */
  addOn?: InputFieldAddOnType;
  /** applies inline-block to container */
  inline?: boolean;
  /** alignment of label and input field */
  alignment?: 'vertical' | 'horizontal';
  /** allow input value to be controlled from props by its parent.
   * NOTE: this flag is intended to be used when using InputField directly.
   * if you are using it with react-final-form, you don't need to change this
   * flag. See FinalFormInput.tsx for more details.
   * */
  isControlled?: boolean;
  children?: JSX.Element;
  password?: boolean;
  onTogglePasswordVisibility?: () => void;

  /**
   * Is this input in a warning state.  Note that error states will take priority
   * over a warning state.
   *
   * @example
   * <InputFieldV3
   *   warn={!meta.touched || !meta.error}
   *
   * @example
   * <InputFieldV3
   *   warn
   *   warningMessage="Too much cake detected in system."
   *
   * @deprecated Use `visualState=warning'` instead.
   */
  warn?: boolean;

  /** Optional warning message to show when warn is true */
  warningMessage?: React.ReactNode;

  visualState?: 'warning' | 'error' | 'success';
}

/**
 * ```import InputFieldV3 from '@zola/zola-ui/build/es/components/Form/inputV3/InputFieldV3';```
 *
 * Base input field component
 *
 *    - wrapper for `<input />` tag that implements error messaging, helper text, helper links, etc.
 *    - same design as InputFieldV2 but styled with Emotion
 */
const InputFieldV3 = forwardRef<HTMLInputElement, InputFieldV3Props>(
  (
    {
      containerClassname,
      id,
      type,
      value: externalValue = '',
      onChange,
      defaultValue,
      label,
      labelProp,
      labelLink,
      placeholder,
      isRequired,
      helperText,
      optionalText,
      tooltip,
      tooltipClassname,
      clear,
      onClear,
      errorMessage,
      successMessage,
      maxChars,
      addOn,
      inline,
      alignment = 'vertical',
      children,
      isControlled = false,
      password,
      onTogglePasswordVisibility,
      warn,
      warningMessage,
      visualState,
      ...rest
    },
    ref
  ) => {
    const initialInputValue = externalValue || defaultValue || '';
    const [inputValue, setInputValue] = useState(initialInputValue);
    const [eyeIcon, setEyeIcon] = useState(<HiddenEye />);
    const [cursorPos, setCursorPos] = useState<number | null>(null);
    const localRef = React.useRef<HTMLInputElement>(null);

    const handleClear = () => {
      if (onClear && typeof onClear === 'function') {
        onClear();
      } else {
        setInputValue('');
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setCursorPos(e.target.selectionStart);

      if (onChange) {
        onChange(e);
      }

      // if the input is controlled, the value of the input will be set on the prop
      if (!isControlled) {
        setInputValue(e.target.value);
      }
    };

    const EyeIconWrapper = (): JSX.Element => (
      <EyeIconContainer onClick={onTogglePasswordVisibility} className="eyeball-icon__container">
        {eyeIcon}
      </EyeIconContainer>
    );

    const ClearIconWrapper = (): JSX.Element => (
      <ClearIconContainer
        color={COLORS3.BLACK_100}
        type="button"
        onClick={handleClear}
        aria-label="clear input"
      >
        <ClearIcon title="clear input" />
      </ClearIconContainer>
    );

    useEffect(() => {
      // update the internal value using external value only when we want to used controlled input
      if (isControlled) {
        setInputValue(externalValue);
      }
    }, [externalValue, isControlled, setInputValue]);

    useEffect(() => {
      if (isControlled) {
        let input;
        if (ref && typeof ref !== 'function' && ref.current) {
          input = ref.current;
        } else if (localRef && localRef.current) {
          input = localRef.current;
        }
        // Note that according to the WHATWG forms spec selectionStart , selectionEnd properties and setSelectionRange method apply only to inputs of types text, search, URL, tel and password
        if (input?.type === 'text' && typeof window !== 'undefined') {
          const { activeElement } = document;
          const isActiveElement = input === activeElement;
          if (document.hasFocus() && isActiveElement) {
            input.setSelectionRange(cursorPos, cursorPos);
          }
        }
      }
    }, [isControlled, inputValue, ref, cursorPos]);

    useEffect(() => {
      if (password) {
        if (type === 'text') {
          setEyeIcon(<Eye />);
        } else {
          setEyeIcon(<HiddenEye />);
        }
      }
    }, [password, type]);

    const phoneNumber = type === 'tel';
    const maxInvalid = maxChars && showMaxCharError(maxChars, inputValue.length);
    const maxWarning = maxChars && showMaxCharWarning(maxChars, inputValue.length, 5);

    const getInputState = (): InputState => {
      // returning state in order of precedence
      if (errorMessage || maxInvalid || visualState === 'error') {
        return 'error';
      }
      if (maxWarning || warn || visualState === 'warning') {
        return 'warn';
      }
      if (successMessage || visualState === 'success') {
        return 'success';
      }
      return '';
    };

    const LabelSection = () =>
      label ? (
        <FormLabel htmlFor={id} {...labelProp}>
          {label}
          {isRequired && <Asterisk>*</Asterisk>}
          {tooltip && <StyledFormTooltip text={tooltip} className={tooltipClassname} />}
          {labelLink && alignment === 'vertical' && renderLink('label', true, labelLink)}
          {optionalText && <OptionalText> {optionalText}</OptionalText>}
        </FormLabel>
      ) : null;

    const MessageSection = () => {
      const renderMessage = () => {
        if (errorMessage) {
          return <Message type="error" text={errorMessage} id={`error-${id}`} />;
        }
        if (maxChars) {
          return (
            <CharCountMessage state={getInputState()}>
              {inputValue.length}/{maxChars}
            </CharCountMessage>
          );
        }
        if (successMessage) {
          return <Message type="success" text={successMessage} />;
        }
        if (helperText) {
          return <HelperText>{helperText}</HelperText>;
        }
        if (warn && warningMessage) {
          return <Message type="warn" text={warningMessage} />;
        }
        return null;
      };

      const message = renderMessage();
      if (!message) return null;

      return <MessageContainer>{message}</MessageContainer>;
    };

    const AddOn = () => {
      switch (addOn) {
        case InputFieldAddOnType.CALENDAR:
          return <CalendarAddOn />;
        case InputFieldAddOnType.CASH:
          return <CashAddOn />;
        case InputFieldAddOnType.GEO_PIN:
          return <GeoPinAddOn />;
        case InputFieldAddOnType.HASHTAG:
          return <HashtagAddOn />;
        case InputFieldAddOnType.LINK:
          return <LinkAddOn />;
        case InputFieldAddOnType.COUPLE_SEARCH:
          return <CoupleSearchAddOn />;
        default:
          return null;
      }
    };

    return (
      <InputFieldContainer
        className={containerClassname}
        inline={inline}
        alignment={alignment}
        data-state={getInputState()}
        hasLabel={Boolean(label)}
      >
        <LabelSection />
        <InputSection>
          <AddOn />
          <Input
            {...rest}
            ref={ref || localRef}
            aria-required={isRequired}
            value={inputValue}
            className="input-field"
            type={type}
            id={id}
            placeholder={phoneNumber ? '(         )   ___-____' : placeholder}
            onChange={handleChange}
            // styling props
            state={getInputState()}
            aria-describedby={errorMessage ? `error-${id}` : undefined}
          />
          {password && <EyeIconWrapper />}
          {clear && inputValue.length !== 0 && <ClearIconWrapper />}
          <MessageSection />
          {labelLink && renderLink('input', alignment === 'vertical', labelLink)}
          {children}
        </InputSection>
      </InputFieldContainer>
    );
  }
);

export default InputFieldV3;
